<template>
  <!--  Remove compact when the filtering is done controller side-->
  <div>
    <EmptyInbox
      v-if="
        displayedTasksAndPages.length === 0 &&
        (eventIdsDisplayed?.length || 0) === 0 &&
        displayedNotices.length === 0
      "
    />
    <div
      v-show="
        displayedTasksAndPages.length > 0 ||
        (eventIdsDisplayed?.length || 0) > 0 ||
        displayedNotices.length > 0
      "
      ref="taskListEl"
      v-click-outside="unselectTask"
      v-hotkey="shortcuts"
      class="inbox-list itemslist"
    >
      <div v-if="displayedNotices.length > 0" class="notices">
        <div class="notices-list">
          <component
            :is="noticeComponent(notice)"
            v-for="notice in displayedNotices"
            :key="notice.id"
            :notice="notice"
          />
        </div>
      </div>

      <div class="event-list">
        <InboxEvent
          v-for="eventId in eventIdsDisplayed"
          :key="eventId"
          :event-id="eventId"
          :is-selected="eventId === selectedTaskId"
          @set-selected-event-el="setSelectedTaskEl"
          @select-event="selectTaskById(eventId)"
        />
        <BaseButton
          v-if="isShowMoreEventsDisplayed"
          class="show-more"
          kind="auxiliary"
          text="Show More"
          @click="showMoreEvents"
        >
          <template #icon>
            <ChevronDownIcon />
          </template>
        </BaseButton>
        <div
          v-if="eventIdsDisplayed && eventIdsDisplayed?.length > 0"
          class="separator"
        />
      </div>
      <div v-for="id in displayedTasksAndPages" :key="id.id">
        <TaskInline
          v-if="id.kind === 'task'"
          class="item"
          :class="{
            selected: id.id === selectedTaskId,
          }"
          :actions="actions"
          :is-selected="id.id === selectedTaskId"
          :is-editing="isEditing"
          :task-id="id.id"
          @select="selectTaskById(id.id)"
          @quit-editing="quitEditing"
          @set-selected-task-el="setSelectedTaskEl"
        />
        <PageInline
          v-else-if="id.kind === 'page'"
          class="item"
          :page-id="id.id"
          :selected="id.id === selectedTaskId"
          :editing="isEditing"
          @select-page="selectTaskById(id.id)"
          @set-selected-page-el="setSelectedTaskEl"
          @quit-editing="quitEditing"
        />
      </div>

      <!-- Infinite scroll trigger for pagination -->
      <div
        v-if="showLoadMore"
        ref="infiniteScrollTrigger"
        class="infinite-scroll-trigger"
      >
        <div v-if="isLoadingMore" class="loading-more">
          <Spinner />
          <span>Loading more items...</span>
        </div>
        <div v-else class="load-trigger">
          <BaseButton
            kind="auxiliary"
            text="Load More Items"
            class="load-more-button"
            @click="loadMore"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { debounce } from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";

import type { EventId, FullNotice, InboxElement } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import EmptyInbox from "@/components/inbox/EmptyInbox.vue";
import InboxAssignmentNotice from "@/components/inbox/InboxAssignmentNotice.vue";
import InboxAutomationAskUserNotice from "@/components/inbox/InboxAutomationAskUserNotice.vue";
import InboxEvent from "@/components/inbox/InboxEvent.vue";
import InboxGenericNotice from "@/components/inbox/InboxGenericNotice.vue";
import InboxGoogleErrorNotice from "@/components/inbox/InboxGoogleErrorNotice.vue";
import InboxIntegrationErrorNotice from "@/components/inbox/InboxIntegrationErrorNotice.vue";
import InboxMention from "@/components/inbox/InboxMention.vue";
import {
  isGoogleErrorNotice,
  isIntegrationErrorNotice,
} from "@/components/inbox/notice";
import * as inject from "@/components/inject";
import PageInline from "@/components/PageInline.vue";
import type { TaskActions } from "@/components/task/types";
import { useTaskList } from "@/components/task/useTaskList";
import TaskInline from "@/components/TaskInline.vue";
import { useReleaser } from "@/ref";

// Pagination configuration
const ITEMS_PER_PAGE = 25;

export default defineComponent({
  components: {
    EmptyInbox,
    ChevronDownIcon,
    BaseButton,
    InboxEvent,
    InboxGenericNotice,
    InboxGoogleErrorNotice,
    InboxIntegrationErrorNotice,
    InboxMention,
    InboxAssignmentNotice,
    InboxAutomationAskUserNotice,
    PageInline,
    TaskInline,
  },
  props: {
    // NOTE: These props are reactive even though their types are arrays.
    // `Inbox.vue` passes refs/computed refs returned by `useInbox()`, and Vue templates
    // auto-unwrap refs when binding props. When the underlying refs change, Vue updates
    // these props and all `computed()` that read them will re-run.
    notices: {
      type: Array as PropType<FullNotice[] | undefined>,
      required: true,
    },
    unansweredMeetings: {
      type: Array as PropType<EventId[] | undefined>,
      required: true,
    },
    unplannedTasks: {
      type: Array as PropType<InboxElement[] | undefined>,
      required: true,
    },
    untitledPages: {
      type: Array as PropType<InboxElement[] | undefined>,
      required: true,
    },
    // Bypass the windowed rendering (meetings display limit, item
    // pagination) and render everything, e.g. while the host's find bar is
    // open so the search covers the whole inbox.
    showAll: { type: Boolean, default: false },
  },

  setup(props) {
    const contextMenu = inject.contextMenu();
    const console = inject.consoleModal();
    const controller = inject.controller();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();

    // Pagination state
    const currentPage = ref(1);
    const isLoadingMore = ref(false);
    const infiniteScrollTrigger = ref<HTMLElement | null>(null);

    // Load more function - define early so it can be used in onMounted
    const loadMore = async () => {
      if (isLoadingMore.value) return;

      isLoadingMore.value = true;
      // Simulate loading delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 300));
      currentPage.value++;
      isLoadingMore.value = false;
    };

    const releaser = useReleaser();

    onMounted(() => {
      const loadMoreDebounced = debounce(async () => {
        if (!isLoadingMore.value) {
          await loadMore();
        }
      }, 200);

      const handleScroll = debounce(() => {
        const triggerElement = infiniteScrollTrigger.value;
        if (!triggerElement || isLoadingMore.value) {
          return;
        }

        const rect = triggerElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isVisible = rect.top < windowHeight + 200; // 200px buffer

        if (isVisible) {
          loadMoreDebounced();
        }
      }, 100);

      // Find and attach to actual scroll container
      const findScrollContainer = () => {
        setTimeout(() => {
          if (infiniteScrollTrigger.value) {
            let element = infiniteScrollTrigger.value.parentElement;
            let level = 0;

            while (element && level < 10) {
              const style = window.getComputedStyle(element);
              const hasScroll =
                style.overflow.includes("scroll") ||
                style.overflow.includes("auto") ||
                style.overflowY.includes("scroll") ||
                style.overflowY.includes("auto");

              if (hasScroll && element.scrollHeight > element.clientHeight) {
                element.addEventListener("scroll", handleScroll, {
                  passive: true,
                });
                break;
              }

              element = element.parentElement;
              level++;
            }
          }
        }, 200);
      };

      // Set up scroll listeners on common containers
      const scrollContainers = [
        window,
        document,
        document.documentElement,
        document.body,
      ];
      scrollContainers.forEach((container) => {
        if (container && container.addEventListener) {
          container.addEventListener("scroll", handleScroll, {
            passive: true,
          });
        }
      });

      // Find the actual scroll container
      findScrollContainer();

      // Also try with delay for DOM readiness
      setTimeout(() => {
        scrollContainers.forEach((container) => {
          if (container && container.addEventListener) {
            // Remove existing listener first to avoid duplicates
            container.removeEventListener("scroll", handleScroll);
            // Add it back
            container.addEventListener("scroll", handleScroll, {
              passive: true,
            });
          }
        });
      }, 100);

      releaser.defer(() => {
        const scrollContainers = [
          window,
          document,
          document.documentElement,
          document.body,
        ];
        scrollContainers.forEach((container) => {
          if (container && container.removeEventListener) {
            container.removeEventListener("scroll", handleScroll);
          }
        });
        loadMoreDebounced.cancel();
        handleScroll.cancel();
      });
    });

    const displayedNotices = computed<FullNotice[]>(() => {
      return props.notices ?? [];
    });

    const noticeComponent = (notice: FullNotice) => {
      if (isIntegrationErrorNotice(notice)) {
        return InboxIntegrationErrorNotice;
      }

      if (isGoogleErrorNotice(notice)) {
        return InboxGoogleErrorNotice;
      }

      if (!("event" in notice)) {
        return InboxGenericNotice;
      }

      switch (notice.event) {
        case "mention":
          return InboxMention;
        case "set_relation":
          return InboxAssignmentNotice;
        case "automation_ask_user":
          return InboxAutomationAskUserNotice;
        default:
          return InboxGenericNotice;
      }
    };

    const eventIds = computed(() => props.unansweredMeetings ?? []);
    const eventsDisplayLimit = ref(2);
    const isShowMoreEventsDisplayed = computed(
      () =>
        eventIdsDisplayed.value &&
        eventIds.value &&
        eventIds.value.length > eventIdsDisplayed.value.length
    );
    const showMoreEvents = () =>
      (eventsDisplayLimit.value = eventsDisplayLimit.value + 5);
    const eventIdsDisplayed = computed(() =>
      props.showAll
        ? eventIds.value
        : eventIds.value?.slice(0, eventsDisplayLimit.value)
    );
    const tasksAndPages = computed<InboxElement[]>(() => [
      ...(props.unplannedTasks ?? []),
      ...(props.untitledPages ?? []),
    ]);

    // Computed properties for pagination
    const displayedTasksAndPages = computed(() => {
      if (props.showAll) return tasksAndPages.value;
      const itemsToShow = currentPage.value * ITEMS_PER_PAGE;
      return tasksAndPages.value.slice(0, itemsToShow);
    });

    const showLoadMore = computed(() => {
      const hasMore =
        displayedTasksAndPages.value.length < tasksAndPages.value.length;

      return hasMore;
    });

    // Reset pagination when filtered items change
    watch(tasksAndPages, () => {
      currentPage.value = 1;
      isLoadingMore.value = false;
    });

    const idsFiltered = computed<string[]>(() => {
      if (eventIdsDisplayed.value && displayedTasksAndPages.value)
        return [
          ...eventIdsDisplayed.value,
          ...displayedTasksAndPages.value.map((id) => id.id),
        ];
      else if (eventIdsDisplayed.value) return [...eventIdsDisplayed.value];
      else if (displayedTasksAndPages.value)
        return [...displayedTasksAndPages.value.map((id) => id.id)];
      else return [];
    });
    const actions: TaskActions = {
      open: true,
      postpone: true,
      notes: true,
      schedule: {
        able: true,
        type: "short",
      },
      hierarchy: true,
    };
    const {
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(idsFiltered, {
      isInputFocused,
      isModalShown,
      controller,
      console,
      contextMenu,
    });
    releaser.defer(releaseTaskList);
    return {
      actions,
      currentPage,
      displayedTasksAndPages,
      displayedNotices,
      eventIdsDisplayed,
      idsFiltered,
      infiniteScrollTrigger,
      isEditing,
      isLoadingMore,
      isShowMoreEventsDisplayed,
      loadMore,
      noticeComponent,
      quitEditing,
      selectTaskById,
      selectedTaskId,
      setSelectedTaskEl,
      shortcuts,
      showLoadMore,
      showMoreEvents,
      taskListEl,
      unselectTask,
    };
  },
});
</script>
<style scoped lang="scss">
@use "@/styles/itemslist.scss";

.inbox-list {
  margin-inline-start: 48px;
  margin-inline-end: 64px;
  padding-top: 12px;

  .notices {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    margin-bottom: 8px;
  }

  .notices-header {
    margin: 0 16px;
  }

  .notices-list {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    margin: 0 16px;
  }

  .separator {
    width: 100%;
    height: 1px;
    margin-bottom: 8px;
    @include themed() {
      background: t($bg-separator);
    }
  }

  .event-list {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    button.base-button.default.auxiliary.show-more {
      margin: 0 16px;
      @include themed() {
        color: t($grey-50);
      }
      :deep(.left) svg .fill {
        @include themed() {
          fill: t($grey-50);
        }
      }
      &:hover {
        @include themed() {
          color: t($grey-70);
        }
        :deep(.left) svg .fill {
          @include themed() {
            fill: t($grey-70);
          }
        }
      }
    }
    .separator {
      width: 100%;
      height: 1px;
      margin-bottom: 8px;
      @include themed() {
        background: t($bg-separator);
      }
    }
  }
}

.infinite-scroll-trigger {
  min-height: 80px;
  height: 80px;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .loading-more {
    display: flex;
    align-items: center;
    gap: 8px;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .load-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
  }
}
</style>
